/**
 * Chart appearance, behind a gear on the chart card.
 *
 * These are the settings that change how the drawn data *looks* - points,
 * gaps, line width, opacity, log scale, and the multi-ship colour mode. None of
 * them touch the request, so each one redraws from cached data with no refetch.
 * That is exactly why they do not belong in the sidebar next to the controls
 * that need a Plot click: mixing the two made it unclear which edits cost a
 * round trip. Anything that changes what is fetched - plot type, axes, filters,
 * overlays, axis bounds - stays in the Plot card.
 *
 * The menu closes on outside click and on Escape, matching the chart card's
 * existing image-export dropdown.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { sourceHasHistory } from '../../state/data-sources.ts';
import { StoreController } from '../../state/controller.ts';
import { activeDataSource, allColumns, comparisonShips, isMultiShip, selectedShip } from '../../state/store.ts';
import {
  plotState,
  setStyleField,
  setLineWidth,
  setPointSize,
  LINE_WIDTH_MIN,
  LINE_WIDTH_MAX,
  POINT_SIZE_MIN,
  POINT_SIZE_MAX,
  setFontSize,
  FONT_SIZE_MIN,
  FONT_SIZE_MAX,
  PLOT_COLOR_MODES,
  HEAT_SCALES,
  setColorScale,
  type HeatScale,
  type PlotColorMode,
  type LiveLayout,
  type LiveMotion,
  type PlotState,
  type PlotStyle,
} from '../../state/plot-state.ts';
import { COLOR_SCALE_OPTIONS } from './plot-constants.ts';
import '../../ui/plain-select.ts';
import {
  periodComparison,
  setComparisonLinkNavigation,
  setComparisonTimeAxis,
} from '../../state/period-comparison.ts';
import { rerenderChartFromLast, resetPeriodComparisonView } from './plot-actions.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import { isTemporalType } from '../../utils/columns.ts';
import { usesGridPanels } from './multi-ship-plot.ts';
import { preferences } from '../../state/preferences.ts';
import {
  hoverTooltipsOn,
  setHoverTooltips,
  setTooltipPosition,
  tooltipPosition,
  type TooltipCorner,
} from '../../state/hover-tooltips.ts';

const CORNER_NAMES: Record<TooltipCorner, string> = {
  tl: 'Top left',
  tr: 'Top right',
  bl: 'Bottom left',
  br: 'Bottom right',
};
/** The label of the drawing section, by what the plot type draws. */
const STYLE_LABELS: Partial<Record<PlotState['plot_type'], string>> = {
  line: 'Lines',
  scatter: 'Points',
  map: 'Points',
  box: 'Boxes',
  heatmap: 'Colours',
  correlation: 'Colours',
  pie: 'Pie',
};
const SIDE_NAMES: Record<TooltipCorner, string> = {
  tl: 'Above left',
  tr: 'Above right',
  bl: 'Below left',
  br: 'Below right',
};

/**
 * Whose chart the menu styles. Explore's by default - the `plotState` atom and
 * its setters, which also remember the choice and redraw Explore's chart. A
 * chart that keeps its own `PlotState` (the SFOC viewer) passes its own, and
 * the menu then offers only what that chart draws: grid, period comparison,
 * precision zoom, gaps and filter thresholds are Explore's chart and stay there.
 */
export interface ChartStyleTarget {
  readonly kind: 'explore' | 'embedded';
  state(): PlotState;
  setStyle<K extends keyof PlotStyle>(key: K, value: PlotStyle[K]): void;
  setColorScale(name: string): void;
  /** More than one ship on the chart, so the ship colour mode applies. */
  multiShip(): boolean;
  /**
   * Which kinds of drawing the chart holds, when that is more than its own
   * plot type - the SFOC chart is curves (line) with measured points behind
   * them (scatter or heatmap), and each gets its own controls. Explore's chart
   * is one kind, its plot type.
   */
  styleKinds?(): Array<PlotState['plot_type']>;
  /** The chart is laid out as panels, so "Panels per row" applies. */
  gridActive?(): boolean;
  /** Re-render the menu when the state behind a non-atom target changes. */
  subscribe(listener: () => void): () => void;
}

export const exploreChartStyle: ChartStyleTarget = {
  kind: 'explore',
  state: () => plotState.value,
  setStyle: setStyleField,
  setColorScale,
  multiShip: isMultiShip,
  subscribe: () => () => {},
};

@customElement('plot-settings-menu')
export class PlotSettingsMenu extends LightElement {
  private store = new StoreController(this, [
    plotState,
    activeDataSource,
    selectedShip,
    comparisonShips,
    periodComparison,
    allColumns,
    preferences,
  ]);

  @property({ attribute: false }) target: ChartStyleTarget = exploreChartStyle;

  @state() private open = false;

  private unwatch: (() => void) | null = null;

  private onDocClick = (e: MouseEvent): void => {
    if (!this.open) return;
    const root = this.querySelector('.chart-settings');
    if (root && !root.contains(e.target as Node)) this.open = false;
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.open = false;
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKey);
    this.unwatch = this.target.subscribe(() => this.requestUpdate());
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKey);
    this.unwatch?.();
    this.unwatch = null;
  }

  private get explore(): boolean {
    return this.target.kind === 'explore';
  }

  private setLineWidth(v: number): void {
    if (this.explore) return setLineWidth(v);
    this.target.setStyle('line_width', Math.max(LINE_WIDTH_MIN, Math.min(LINE_WIDTH_MAX, Math.round(v))));
  }

  private setPointSize(v: number): void {
    if (this.explore) return setPointSize(v);
    this.target.setStyle(
      'scatter_point_size',
      Math.max(POINT_SIZE_MIN, Math.min(POINT_SIZE_MAX, Math.round(v))),
    );
  }

  private setFontSize(v: number): void {
    if (this.explore) return setFontSize(v);
    this.target.setStyle('font_size', Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, Math.round(v))));
  }

  /* ── Style toggles ────────────────────────────────────── */

  private renderStepper(
    label: string,
    value: number,
    min: number,
    max: number,
    set: (v: number) => void,
  ): TemplateResult {
    return html`
      <span class="inline-label-group stepper-wrap">
        <span class="inline-label">${label}</span>
        <span class="stepper">
          <button
            type="button"
            class="stepper-btn"
            ?disabled=${value <= min}
            @click=${() => set(value - 1)}
          >
            −
          </button>
          <span class="stepper-value">${value}</span>
          <button
            type="button"
            class="stepper-btn"
            ?disabled=${value >= max}
            @click=${() => set(value + 1)}
          >
            +
          </button>
        </span>
      </span>
    `;
  }

  /**
   * Multi-ship colour allocation. Hidden while a single ship is plotted - there
   * is nothing for it to allocate, and the per-signal palette already applies.
   */
  private renderColorMode(s: PlotState): TemplateResult | typeof nothing {
    if (
      !this.target.multiShip() ||
      (this.explore && usesGridPanels(s.plot_type, s.view_mode))
    ) {
      return nothing;
    }
    return html`
      <div class="style-row">
        <label class="inline-label" for="plot-color-mode">Ship colours</label>
        <plain-select
          id="plot-color-mode"
          .value=${s.style.color_mode}
          .options=${PLOT_COLOR_MODES.map((m) => ({ value: m.value, label: m.label }))}
          .select=${(v: string) => this.target.setStyle('color_mode', v as PlotColorMode)}
        ></plain-select>
      </div>
    `;
  }

  private renderStyles(s: PlotState, type: PlotState['plot_type']): TemplateResult | typeof nothing {
    if (type === 'number' || type === 'gauge' || type === 'sankey' || type === 'table') return nothing;
    if (type === 'line') {
      return html`
        <div class="toggle-grid">
          <label
            ><input
              type="checkbox"
              .checked=${s.style.show_points}
              @change=${(e: Event) =>
                this.target.setStyle('show_points', (e.target as HTMLInputElement).checked)}
            />
            Show points</label
          >
          ${this.explore
            ? html`<label
                  ><input
                    type="checkbox"
                    .checked=${s.style.connect_gaps}
                    @change=${(e: Event) =>
                      this.target.setStyle('connect_gaps', (e.target as HTMLInputElement).checked)}
                  />
                  Connect gaps</label
                >
                <label
                  ><input
                    type="checkbox"
                    .checked=${s.style.show_thresholds}
                    @change=${(e: Event) =>
                      this.target.setStyle(
                        'show_thresholds',
                        (e.target as HTMLInputElement).checked,
                      )}
                  />
                  Show filter thresholds</label
                >`
            : nothing}
          <label
            ><input
              type="checkbox"
              .checked=${s.style.log_scale}
              @change=${(e: Event) =>
                this.target.setStyle('log_scale', (e.target as HTMLInputElement).checked)}
            />
            Log scale</label
          >
          ${this.renderStepper(
            'Line width',
            s.style.line_width,
            LINE_WIDTH_MIN,
            LINE_WIDTH_MAX,
            (v) => this.setLineWidth(v),
          )}
        </div>
      `;
    }
    if (type === 'scatter') {
      return html`
        <div class="style-row">
          <label>Point alpha</label>
          <input
            type="range"
            min="0.05"
            max="1"
            step="0.05"
            .value=${String(s.style.scatter_alpha)}
            @input=${(e: Event) =>
              this.target.setStyle('scatter_alpha', parseFloat((e.target as HTMLInputElement).value))}
          />
          <span class="mono-val">${s.style.scatter_alpha.toFixed(2)}</span>
          ${this.renderStepper(
            'Point size',
            s.style.scatter_point_size,
            POINT_SIZE_MIN,
            POINT_SIZE_MAX,
            (v) => this.setPointSize(v),
          )}
        </div>
        ${this.explore
          ? html`<div class="style-row">
          <label class="inline-label"
            ><input
              type="checkbox"
              .checked=${s.style.show_diagonal}
              @change=${(e: Event) =>
                this.target.setStyle('show_diagonal', (e.target as HTMLInputElement).checked)}
            />
            Show diagonal</label
          >
        </div>`
          : nothing}
      `;
    }
    if (type === 'heatmap') {
      return html`
        <div class="style-row">
          <label class="inline-label" for="heat-ramp">Colour ramp</label>
          <plain-select
            id="heat-ramp"
            .value=${s.color_scale}
            .options=${COLOR_SCALE_OPTIONS}
            .select=${(v: string) => this.target.setColorScale(v)}
          ></plain-select>
        </div>
        <div class="style-row">
          <label class="inline-label" for="heat-scale">Colour scaling</label>
          <plain-select
            id="heat-scale"
            .value=${s.style.heat_scale}
            .options=${HEAT_SCALES.map((o) => ({ value: o.value, label: o.label }))}
            .select=${(v: string) => this.target.setStyle('heat_scale', v as HeatScale)}
          ></plain-select>
        </div>
      `;
    }
    if (type === 'pie') {
      return html`
        <div class="style-row">
          <label class="inline-label"
            ><input
              type="checkbox"
              .checked=${s.style.pie_donut}
              @change=${(e: Event) =>
                this.target.setStyle('pie_donut', (e.target as HTMLInputElement).checked)}
            />
            Donut, total in the middle</label
          >
        </div>
        ${s.pie_mode === 'bands'
          ? html`<div class="style-row">
              <label class="inline-label" for="pie-ramp">Colour ramp</label>
              <plain-select
                id="pie-ramp"
                .value=${s.color_scale}
                .options=${COLOR_SCALE_OPTIONS}
                .select=${(v: string) => this.target.setColorScale(v)}
              ></plain-select>
            </div>`
          : nothing}
      `;
    }
    if (type === 'correlation') {
      return html`
        <div class="style-row">
          <label class="inline-label" for="corr-ramp">Colour ramp</label>
          <plain-select
            id="corr-ramp"
            .value=${s.color_scale}
            .options=${COLOR_SCALE_OPTIONS}
            .select=${(v: string) => this.target.setColorScale(v)}
          ></plain-select>
        </div>
        <div class="style-row">
          <label class="inline-label"
            ><input
              type="checkbox"
              .checked=${s.style.corr_triangle}
              @change=${(e: Event) =>
                this.target.setStyle('corr_triangle', (e.target as HTMLInputElement).checked)}
            />
            Lower triangle only</label
          >
        </div>
      `;
    }
    if (type === 'box') {
      // Outliers are drawn as points, so the scatter's point controls apply to
      // them; the box outline and whiskers follow the line width.
      return html`
        <div class="style-row">
          ${this.renderStepper(
            'Line width',
            s.style.line_width,
            LINE_WIDTH_MIN,
            LINE_WIDTH_MAX,
            (v) => this.setLineWidth(v),
          )}
        </div>
        <div class="style-row">
          <label>Outlier alpha</label>
          <input
            type="range"
            min="0.05"
            max="1"
            step="0.05"
            .value=${String(s.style.scatter_alpha)}
            @input=${(e: Event) =>
              this.target.setStyle('scatter_alpha', parseFloat((e.target as HTMLInputElement).value))}
          />
          <span class="mono-val">${s.style.scatter_alpha.toFixed(2)}</span>
          ${this.renderStepper(
            'Outlier size',
            s.style.scatter_point_size,
            POINT_SIZE_MIN,
            POINT_SIZE_MAX,
            (v) => this.setPointSize(v),
          )}
        </div>
      `;
    }
    if (type === 'map') {
      return html`
        <div class="style-row">
          <label>Point alpha</label>
          <input
            type="range"
            min="0.05"
            max="1"
            step="0.05"
            .value=${String(s.style.scatter_alpha)}
            @input=${(e: Event) =>
              this.target.setStyle('scatter_alpha', parseFloat((e.target as HTMLInputElement).value))}
          />
          <span class="mono-val">${s.style.scatter_alpha.toFixed(2)}</span>
          ${this.renderStepper(
            'Point size',
            s.style.scatter_point_size,
            POINT_SIZE_MIN,
            POINT_SIZE_MAX,
            (v) => this.setPointSize(v),
          )}
        </div>
      `;
    }
    return nothing;
  }

  /**
   * Hover behaviour. Applies to every chart type that draws series, so it sits
   * above the per-type block rather than inside it. Map has no series to focus.
   */
  /**
   * Chart text size - one control for every plot type.
   *
   * Sits with the shared settings rather than per type because it is the same
   * question everywhere, and because charts are read side by side: text that
   * changed size between a line chart and the matrix next to it would look like
   * a mistake rather than a setting.
   */
  /**
   * Live line charts: where the newest readings show
   * (`docs/design/sketches/live-last-value.html`). Explore only, and only while
   * it reads a source without history.
   */
  private renderLive(s: PlotState): TemplateResult | typeof nothing {
    if (!this.explore || s.plot_type !== 'line' || sourceHasHistory(activeDataSource.value)) return nothing;
    return html`
      <div class="chart-settings-field">
        <label for="live-layout">Live layout</label>
        <select
          id="live-layout"
          .value=${s.style.live_layout}
          @change=${(e: Event) =>
            this.target.setStyle('live_layout', (e.target as HTMLSelectElement).value as LiveLayout)}
        >
          <option value="end" ?selected=${s.style.live_layout === 'end'}>Value at line end</option>
          <option value="center" ?selected=${s.style.live_layout === 'center'}>Big value</option>
          <option value="now" ?selected=${s.style.live_layout === 'now'}>Now in the middle</option>
        </select>
      </div>
      <div class="chart-settings-field">
        <label for="live-motion" title="How the time axis moves between new readings">Motion</label>
        <select
          id="live-motion"
          .value=${s.style.live_motion}
          @change=${(e: Event) =>
            this.target.setStyle('live_motion', (e.target as HTMLSelectElement).value as LiveMotion)}
        >
          <option value="smooth" ?selected=${s.style.live_motion === 'smooth'}>Smooth</option>
          <option value="steps" ?selected=${s.style.live_motion === 'steps'}>1 s steps</option>
          <option value="off" ?selected=${s.style.live_motion === 'off'}>Off - move on new data</option>
        </select>
      </div>
      ${s.style.live_layout === 'now'
        ? html`<div class="style-row">
            <label class="inline-label" title="The NOW label with the time, on top of the now line">
              <input
                type="checkbox"
                .checked=${s.style.live_now_label}
                @change=${(e: Event) =>
                  this.target.setStyle('live_now_label', (e.target as HTMLInputElement).checked)}
              />
              NOW label on the line
            </label>
          </div>`
        : nothing}
    `;
  }

  private renderTextSize(s: PlotState): TemplateResult {
    return html`
      <div class="style-row">
        ${this.renderStepper(
          'Text size',
          s.style.font_size,
          FONT_SIZE_MIN,
          FONT_SIZE_MAX,
          (v) => this.setFontSize(v),
        )}
        <span class="toggle-hint">axes, labels, legend and tooltip</span>
      </div>
    `;
  }

  /**
   * The pointer readout. The one row here that is not the chart's own: it is
   * this person's setting for every chart, so it reads and writes the account
   * preference whichever target the menu is styling. A number tile has no
   * pointer readout to switch.
   *
   * Off, beside the pointer on a chosen side, or pinned to a chosen corner of
   * the plot; each mode keeps its own corner (sketch:
   * docs/design/sketches/chart-tooltip-position.html).
   */
  private renderTooltip(s: PlotState): TemplateResult | typeof nothing {
    if (s.plot_type === 'number' || s.plot_type === 'gauge' || s.plot_type === 'table') return nothing;
    const on = hoverTooltipsOn();
    const pos = tooltipPosition();
    const mode = on ? pos.mode : 'off';
    const choose = (next: 'off' | 'follow' | 'fixed'): void => {
      if (next === 'off') return setHoverTooltips(false);
      if (!on) setHoverTooltips(true);
      setTooltipPosition({ mode: next });
    };
    const radio = (value: 'off' | 'follow' | 'fixed', label: string) => html`
      <label class="inline-label">
        <input type="radio" name="tooltip-mode" .checked=${mode === value} @change=${() => choose(value)} />
        ${label}
      </label>
    `;
    const picker = (
      kind: 'follow' | 'fixed',
      value: TooltipCorner,
      names: Record<TooltipCorner, string>,
      set: (c: TooltipCorner) => void,
    ) => html`
      <div class="tt-corner-row ${mode === kind ? '' : 'is-off'}" aria-disabled=${mode !== kind}>
        <div class="tt-corners ${kind === 'follow' ? 'around' : ''}" role="group" aria-label=${kind === 'follow' ? 'Side of the pointer' : 'Corner'}>
          ${(['tl', 'tr', 'bl', 'br'] as const).map(
            (c) => html`<button
              type="button"
              class=${c}
              aria-label=${names[c]}
              title=${names[c]}
              aria-pressed=${value === c}
              ?disabled=${mode !== kind}
              @click=${() => set(c)}
            ></button>`,
          )}
        </div>
        <span class="toggle-hint">${names[value]}</span>
      </div>
    `;
    return html`
      <div class="chart-settings-field tt-position" title="The values shown at the pointer. Applies to every chart you look at; the crosshair and legend stay.">
        ${radio('off', 'Off')}
        ${radio('follow', 'Follow pointer')}
        ${picker('follow', pos.side, SIDE_NAMES, (side) => setTooltipPosition({ side }))}
        ${radio('fixed', 'Fixed in a corner')}
        ${picker('fixed', pos.corner, CORNER_NAMES, (corner) => setTooltipPosition({ corner }))}
        <label class="inline-label tt-fade ${mode === 'fixed' ? '' : 'is-off'}">
          <input
            type="checkbox"
            .checked=${pos.fade}
            ?disabled=${mode !== 'fixed'}
            @change=${(e: Event) => setTooltipPosition({ fade: (e.target as HTMLInputElement).checked })}
          />
          Fade when the pointer is under it
        </label>
      </div>
    `;
  }

  private renderInteraction(s: PlotState): TemplateResult | typeof nothing {
    if (s.plot_type === 'map' || s.plot_type === 'number' || s.plot_type === 'gauge' || s.plot_type === 'sankey' || s.plot_type === 'table') return nothing;
    // Precision zoom (the axis sliders) narrows the view of the single chart
    // without a refetch - a zoom, not a filter; slicers are the filter. Grid
    // panels and period comparisons draw through their own renderer, which does
    // not take them yet - so the switch is only offered where it works.
    const sliders =
      this.explore &&
      (s.plot_type === 'line' || s.plot_type === 'scatter') &&
      !periodComparison.value &&
      !(isMultiShip() && s.view_mode === 'grid');
    // A pie has slices, not series: hover already pulls a slice out, and its
    // legend has nothing to select - only the Legend switch itself applies.
    // A box plot is the same: its legend is the painted ship key of an overlay.
    const pie = s.plot_type === 'pie' || s.plot_type === 'box';
    return html`
      ${pie
        ? nothing
        : html`<div class="style-row">
            <label class="inline-label">
              <input
                type="checkbox"
                .checked=${s.style.focus_hover}
                @change=${(e: Event) =>
                  this.target.setStyle('focus_hover', (e.target as HTMLInputElement).checked)}
              />
              Focus on hovered series
            </label>
          </div>`}
      <div class="style-row">
        <label
          class="inline-label"
          title=${s.plot_type === 'box'
            ? 'The ship key above an overlay of several ships.'
            : pie
            ? 'The slice names, values and shares beside the pie.'
            : 'The series rows under the chart. They only appear with two or more series.'}
        >
          <input
            type="checkbox"
            .checked=${s.style.show_legend}
            @change=${(e: Event) =>
              this.target.setStyle('show_legend', (e.target as HTMLInputElement).checked)}
          />
          Legend
        </label>
      </div>
      ${s.style.show_legend && !pie && this.explore
        ? html`<div class="style-row">
            <label
              class="inline-label"
              title="The Select all / Deselect all buttons inside the legend"
            >
              <input
                type="checkbox"
                .checked=${s.style.legend_tools}
                @change=${(e: Event) =>
                  this.target.setStyle('legend_tools', (e.target as HTMLInputElement).checked)}
              />
              Select all / Deselect all buttons
            </label>
          </div>`
        : nothing}
      ${sliders
        ? html`<div class="style-row">
            <label class="inline-label" title="Range bars along X and Y for exact zooming - a view change only, nothing is filtered or re-queried">
              <input
                type="checkbox"
                .checked=${s.style.precision_zoom}
                @change=${(e: Event) =>
                  this.target.setStyle('precision_zoom', (e.target as HTMLInputElement).checked)}
              />
              Precision zoom
            </label>
          </div>`
        : nothing}
    `;
  }

  /**
   * Grid-only options. Shared Y is what makes two panels comparable - with
   * independent scales a ship peaking at 18 and one peaking at 1.1 draw as the
   * same shape - so it is on by default and the toggle is the escape hatch.
   */
  private renderPanelsPerRow(s: PlotState): TemplateResult {
    const columns = s.style.grid_columns;
    return html`
      <div class="chart-settings-field">
        <label for="grid-columns">Panels per row</label>
        <select
          id="grid-columns"
          .value=${columns == null ? 'auto' : String(columns)}
          @change=${(e: Event) => {
            const raw = (e.target as HTMLSelectElement).value;
            this.target.setStyle('grid_columns', raw === 'auto' ? null : Number(raw));
          }}
        >
          <option value="auto" ?selected=${columns == null}>Automatic</option>
          ${[1, 2, 3, 4].map(
            (n) => html`<option value=${n} ?selected=${columns === n}>${n}</option>`,
          )}
        </select>
      </div>
    `;
  }

  private renderGridOptions(s: PlotState): TemplateResult | typeof nothing {
    if (!this.explore) {
      // A chart outside Explore that lays out panels gets the one grid control
      // that means the same there; its panels share a scale by design.
      return this.target.gridActive?.() ? this.renderPanelsPerRow(s) : nothing;
    }
    if (!periodComparison.value && (!isMultiShip() || s.view_mode !== 'grid')) return nothing;
    return html`
      ${s.plot_type === 'number' ? nothing : html`<div class="style-row">
        <label class="inline-label">
          <input
            type="checkbox"
            .checked=${s.style.grid_shared_y}
            @change=${(e: Event) =>
              this.target.setStyle('grid_shared_y', (e.target as HTMLInputElement).checked)}
          />
          Shared Y across panels
        </label>
      </div>`}
      ${this.renderPanelsPerRow(s)}
    `;
  }

  private renderPeriodOptions(s: PlotState): TemplateResult | typeof nothing {
    const comparison = periodComparison.value;
    if (!comparison || !this.explore) return nothing;
    const xType = allColumns.value.find((column) => column.name === s.x_column)?.type;
    const timeX = s.x_column === 'timestampNDC' || (xType ? isTemporalType(xType) : false);
    return html`
      ${timeX ? html`<div class="chart-settings-field">
        <label for="period-time-axis">Time axis</label>
        <select
          id="period-time-axis"
          .value=${comparison.timeAxis}
          @change=${(event: Event) => {
            setComparisonTimeAxis((event.target as HTMLSelectElement).value as 'elapsed' | 'utc');
            if (!resetPeriodComparisonView()) rerenderChartFromLast();
            syncToUrl();
          }}
        >
          <option value="elapsed">Elapsed from start</option>
          <option value="utc">Actual dates (UTC)</option>
        </select>
      </div>` : nothing}
      <div class="style-row">
        <label class="inline-label" title=${timeX && comparison.timeAxis === 'utc'
          ? 'Use elapsed time to link periods.'
          : 'Keep the cursor and x zoom aligned across panels'}>
          <input
            type="checkbox"
            ?disabled=${timeX && comparison.timeAxis === 'utc'}
            .checked=${comparison.linkNavigation && !(timeX && comparison.timeAxis === 'utc')}
            @change=${(event: Event) => {
              setComparisonLinkNavigation((event.target as HTMLInputElement).checked);
              rerenderChartFromLast();
              syncToUrl();
            }}
          />
          Link cursor and zoom
        </label>
      </div>
    `;
  }

  /**
   * One labelled block, drawn only if a part has something to show. Sections go
   * in order of importance (docs/design/sketches/chart-settings-sections.html).
   */
  private section(
    label: string,
    parts: Array<TemplateResult | typeof nothing>,
    scope?: string,
  ): TemplateResult | typeof nothing {
    const shown = parts.filter((p) => p !== nothing);
    if (!shown.length) return nothing;
    return html`<div class="chart-settings-sec">
      <div class="chart-settings-sec-label"><span>${label}</span>${scope ? html`<span class="scope">${scope}</span>` : nothing}</div>
      ${shown}
    </div>`;
  }

  override render(): TemplateResult {
    const s = this.target.state();
    const kinds = this.target.styleKinds?.() ?? [s.plot_type];
    return html`
      <div class="chart-settings">
        <button
          class="chart-icon-btn chart-settings-btn ${this.open ? 'active' : ''}"
          title="Chart appearance - points, colours, scales"
          aria-haspopup="menu"
          aria-expanded=${this.open ? 'true' : 'false'}
          @click=${(e: Event) => {
            e.stopPropagation();
            this.open = !this.open;
          }}
        >
          <span class="chart-action-glyph" aria-hidden="true">⚙</span> Chart
        </button>
        ${this.open
          ? html`<div class="chart-settings-menu">
              <div class="chart-settings-title">Appearance</div>
              ${this.section('Layout', [
                this.renderPeriodOptions(s),
                this.renderGridOptions(s),
                this.renderColorMode(s),
                this.renderLive(s),
              ])}
              ${kinds.map((t) => this.section(STYLE_LABELS[t] ?? 'Style', [this.renderStyles(s, t)]))}
              ${this.section('Legend and hover', [this.renderInteraction(s)])}
              ${this.section('Text', [this.renderTextSize(s)])}
              ${this.section('Tooltip', [this.renderTooltip(s)], 'All your charts')}
              <div class="chart-settings-note">
                Redraws instantly - no re-query.
              </div>
            </div>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plot-settings-menu': PlotSettingsMenu;
  }
}
